import React, { useEffect } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import ErrorPage from '@pages/errorPage/ErrorPage';
import { useSessionStorage } from 'usehooks-ts';
import { BASE_PATH } from '@/configuration';

interface AppErrorBoundaryProps {
    children: React.ReactNode;
}

// Detect if error is related to dynamic chunk loading
const isChunkError = (error: unknown): boolean => {
    const message = typeof error === 'string' ? error : ((error as Error)?.message ?? error?.toString() ?? '');

    return (
        message.includes('Loading chunk') ||
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Loading CSS chunk') ||
        message.includes('Unable to preload CSS') ||
        message.includes('ChunkLoadError')
    );
};

const HAS_RELOADED_KEY = 'custom-error-boundary-reloaded';

// Global handler for unhandled errors
const useGlobalChunkErrorHandler = (hasReloaded: boolean, setHasReloaded: (value: boolean) => void) => {
    useEffect(() => {
        const handler = (event: PromiseRejectionEvent | ErrorEvent) => {
            const reason =
                (event as PromiseRejectionEvent).reason || (event as ErrorEvent).error || (event as ErrorEvent).message;

            if (reason && isChunkError(reason)) {
                if (!hasReloaded) {
                    console.warn('Global chunk error detected. Reloading page once...');
                    event.preventDefault?.();
                    setHasReloaded(true);
                    window.location.reload();
                } else {
                    console.warn('Chunk error occurred again after reload. Showing ErrorPage...');
                }
            }
        };

        window.addEventListener('unhandledrejection', handler as any);
        window.addEventListener('error', handler as any);

        return () => {
            window.removeEventListener('unhandledrejection', handler as any);
            window.removeEventListener('error', handler as any);
        };
    }, [hasReloaded, setHasReloaded]);
};

// Error fallback logic
const AppErrorFallback: React.FC<
    FallbackProps & { hasReloaded: boolean; setHasReloaded: (value: boolean) => void }
> = ({ error, hasReloaded, setHasReloaded, resetErrorBoundary }) => {
    if (isChunkError(error)) {
        if (!hasReloaded) {
            console.warn('React Boundary: Chunk error detected. Reloading page once...');
            setHasReloaded(true);
            window.location.reload();
            return null;
        }

        console.warn('React Boundary: Chunk error again after reload. Showing ErrorPage.');
    }

    return <ErrorPage error={error} resetErrorBoundary={resetErrorBoundary} />;
};

export const AppErrorBoundary: React.FC<AppErrorBoundaryProps> = ({ children }) => {
    const [hasReloaded, setHasReloaded] = useSessionStorage<boolean>(HAS_RELOADED_KEY, false);

    useGlobalChunkErrorHandler(hasReloaded, setHasReloaded);

    return (
        <ErrorBoundary
            FallbackComponent={(props) => (
                <AppErrorFallback {...props} hasReloaded={hasReloaded} setHasReloaded={setHasReloaded} />
            )}
            onReset={() => window.location.replace(BASE_PATH)}
        >
            {children}
        </ErrorBoundary>
    );
};
